import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Button,
  Input,
  InputNumber,
  Spin,
  message,
  Form,
  Tabs,
  Tag,
  Switch,
  Collapse,
  Pagination,
  Popconfirm,
  Select,
} from "antd";
import {
  LoadingOutlined,
  SaveOutlined,
  ReloadOutlined,
  SearchOutlined,
  CodeOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import SupportLayout from "../support/SupportLayout";
import useAdminPortalData from "./hooks/useAdminPortalData";

const formatRateConfig = (item) => {
  const key = item.type;
  const rawDesc = item.description;

  const readableTitle = key
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");

  let prefix = null;
  let suffix = null;

  if (
    key.includes("MIN_ORDER") ||
    key.includes("MINIMUM_ORDER") ||
    key.includes("MIN_DISCOUNT") ||
    key.includes("BONUS") ||
    key.includes("SURCHARGE_PER_KM") ||
    key.includes("MINIMUM_FEE") ||
    key.includes("CAPPED") ||
    key.includes("TRANSFER_MIN") ||
    key.includes("MIN_TRANSFER") ||
    key.includes("WELCOME_CASH") ||
    key.includes("KYC")
  ) {
    prefix = "₦";
  } else if (
    key.includes("MARKUP") ||
    key.includes("PERCENTAGE") ||
    key.includes("DISCOUNT") ||
    key.includes("COMMISSION") ||
    key.includes("FEE")
  ) {
    suffix = "%";
  } else if (key.includes("RADIUS") || key.includes("DISTANCE_SURCHARGE_THRESHOLD")) {
    suffix = "km";
  }

  let categoryKey = "SYSTEM";
  let categoryLabel = "System & Automation";

  if (key.includes("MIN_ORDER") || key.includes("MINIMUM_ORDER") || key.includes("MIN_DISCOUNT") || key.includes("DISCOUNT_PERCENTAGE")) {
    categoryKey = "MINIMUM_ORDERS";
    categoryLabel = "Minimum Orders & Discounts";
  } else if (key.includes("MARKUP") || key.includes("SERVICE_CHARGE")) {
    categoryKey = "MARKUPS";
    categoryLabel = "Vendor Markups & Fees";
  } else if (key.includes("DELIVERY") || key.includes("DISTANCE")) {
    categoryKey = "DELIVERY";
    categoryLabel = "Delivery & Logistics";
  } else if (
    key.includes("WALLET") ||
    key.includes("TRANSFER") ||
    key.includes("BONUS") ||
    key.includes("REFERRAL") ||
    key.includes("DEPOSIT") ||
    key.includes("KYC")
  ) {
    categoryKey = "FINANCIAL";
    categoryLabel = "Wallet & Financial";
  }

  return {
    key,
    label: readableTitle,
    description: rawDesc,
    prefix,
    suffix,
    categoryKey,
    categoryLabel,
    isActive: item.isActive !== false,
    raw: item,
  };
};

const CollapsibleRateList = ({
  items,
  formValues,
  formDescriptions,
  onFieldValueChange,
  onDescriptionChange,
  onSaveSingle,
  onHideSingle,
  onUnhideSingle,
  updatingKey,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [items]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, currentPage, pageSize]);

  const collapseItems = paginatedItems.map((field) => {
    const rawVal = formValues[field.key];
    const val = rawVal !== undefined ? Number(rawVal) : undefined;
    const displayVal = val !== undefined ? val : field.raw.value;
    const isSavingThisKey = updatingKey === field.key;

    const currentDesc =
      formDescriptions[field.key] !== undefined
        ? formDescriptions[field.key]
        : field.description || "";
    const displayTitle = currentDesc.trim() ? currentDesc : field.label;

    return {
      key: field.key,
      label: (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 w-full pr-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-gilroyBold text-gray-900 text-[15px]">
              {displayTitle}
            </span>
            <span className="text-[13px] text-gray-400 font-mono">
              ({field.key})
            </span>
            {!field.isActive && (
              <Tag color="default" className="text-[12px] font-semibold px-2 py-0.5 rounded-md m-0 text-gray-500">
                Hidden
              </Tag>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Tag color="blue" className="text-[14px] font-semibold px-3 py-0.5 rounded-md m-0">
              {field.prefix ? `${field.prefix} ${displayVal}` : field.suffix ? `${displayVal} ${field.suffix}` : displayVal}
            </Tag>
          </div>
        </div>
      ),
      children: (
        <div className="bg-gray-50 p-4 rounded-xl space-y-4 border border-gray-100">
          <div>
            <label className="block font-gilroyBold text-gray-800 text-[14px] mb-1">
              Easy Identification Name / Description
            </label>
            <Input
              className="w-full text-[15px] rounded-lg border-gray-300"
              size="large"
              value={currentDesc}
              placeholder="e.g. Restaurant Minimum Order Amount"
              onChange={(e) => onDescriptionChange(field.key, e.target.value)}
            />
          </div>

          <div>
            <label className="block font-gilroyBold text-gray-800 text-[14px] mb-1">
              Rate Setting Value
            </label>
            <InputNumber
              className="w-full text-[15px] rounded-lg border-gray-300"
              size="large"
              value={val}
              formatter={(v) =>
                field.prefix
                  ? `${field.prefix} ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : field.suffix
                  ? `${v} ${field.suffix}`
                  : `${v}`
              }
              parser={(v) => v.replace(/[^\d.]/g, "")}
              onChange={(v) => onFieldValueChange(field.key, v)}
            />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-gray-200">
            <Tag color={field.raw?.creditAccountType === "BONUS" ? "gold" : "cyan"} className="text-[12px] px-2.5 py-0.5 rounded-md">
              Credit Account: {field.raw?.creditAccountType || "WALLET"}
            </Tag>
            <div className="flex items-center gap-2">
              {!field.isActive ? (
                <Button
                  type="default"
                  icon={<EyeOutlined />}
                  loading={isSavingThisKey}
                  onClick={() => onUnhideSingle(field.key)}
                  className="font-gilroyBold px-4 h-9 rounded-lg text-[14px] text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                >
                  Unhide
                </Button>
              ) : (
                <Popconfirm
                  title="Hide Rate Config"
                  description={`Are you sure you want to hide "${field.key}" from admin view?`}
                  onConfirm={() => onHideSingle(field.key)}
                  okText="Yes, Hide"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    danger
                    icon={<EyeInvisibleOutlined />}
                    className="font-gilroyBold px-4 h-9 rounded-lg text-[14px]"
                  >
                    Hide
                  </Button>
                </Popconfirm>
              )}
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={isSavingThisKey}
                onClick={() => onSaveSingle(field.key)}
                className="bg-[#0e1726] hover:bg-[#1b2a4a] text-white font-gilroyBold px-6 h-9 rounded-lg text-[14px] border-none"
              >
                Save Setting
              </Button>
            </div>
          </div>
        </div>
      ),
    };
  });

  return (
    <div className="mt-4 space-y-4">
      <Collapse
        items={collapseItems}
        className="bg-white border-gray-200 rounded-2xl overflow-hidden shadow-sm"
      />

      {items.length > pageSize && (
        <div className="flex justify-center mt-6">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={items.length}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
            className="text-[15px]"
          />
        </div>
      )}
    </div>
  );
};

const AdminRateSettings = () => {
  const { config, loading, fetchConfig, updateConfig, deleteConfig, unhideConfig } = useAdminPortalData();
  const [formValues, setFormValues] = useState({});
  const [formDescriptions, setFormDescriptions] = useState({});
  const [jsonText, setJsonText] = useState("");
  const [jsonMode, setJsonMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingKey, setUpdatingKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [adminFilter, setAdminFilter] = useState("normal");
  const [activeTab, setActiveTab] = useState("all_rates");

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const rawConfigsArray = useMemo(() => {
    if (!config) return [];
    if (Array.isArray(config.configs)) return config.configs;
    if (config.map) {
      return Object.entries(config.map).map(([type, value]) => ({
        type,
        value: String(value),
      }));
    }
    return Object.entries(config).map(([type, value]) => ({
      type,
      value: String(value),
    }));
  }, [config]);

  const formattedConfigs = useMemo(() => {
    return rawConfigsArray
      .map(formatRateConfig)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rawConfigsArray]);

  const configMap = useMemo(() => {
    const map = {};
    for (const item of rawConfigsArray) {
      map[item.type] = item.value;
    }
    return map;
  }, [rawConfigsArray]);

  useEffect(() => {
    setFormValues(configMap);

    const descMap = {};
    for (const item of rawConfigsArray) {
      descMap[item.type] = item.description || "";
    }
    setFormDescriptions(descMap);

    setJsonText(JSON.stringify(configMap, null, 2));
  }, [configMap, rawConfigsArray]);

  const filteredFormattedConfigs = useMemo(() => {
    let list = formattedConfigs;

    if (adminFilter === "normal") {
      list = list.filter((item) => item.isActive !== false);
    } else if (adminFilter === "hidden") {
      list = list.filter((item) => item.isActive === false);
    }

    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (item) =>
        item.key.toLowerCase().includes(q) ||
        (item.description && item.description.toLowerCase().includes(q)) ||
        (formDescriptions[item.key] && formDescriptions[item.key].toLowerCase().includes(q)) ||
        String(item.raw.value).toLowerCase().includes(q)
    );
  }, [formattedConfigs, adminFilter, searchQuery, formDescriptions]);

  const dynamicCategories = useMemo(() => {
    const groups = {};
    for (const item of filteredFormattedConfigs) {
      const cat = item.categoryKey;
      if (!groups[cat]) {
        groups[cat] = {
          key: cat,
          label: item.categoryLabel,
          items: [],
        };
      }
      groups[cat].items.push(item);
    }
    for (const catKey of Object.keys(groups)) {
      groups[catKey].items.sort((a, b) => a.label.localeCompare(b.label));
    }
    return Object.values(groups).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [filteredFormattedConfigs]);

  const handleFieldValueChange = (key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDescriptionChange = (key, text) => {
    setFormDescriptions((prev) => ({
      ...prev,
      [key]: text,
    }));
  };

  const handleSaveSingleKey = async (key) => {
    try {
      setUpdatingKey(key);
      const val = formValues[key];
      const desc = formDescriptions[key];
      const result = await updateConfig({
        type: key,
        value: String(val !== undefined ? val : ""),
        description: String(desc !== undefined ? desc : ""),
      });
      if (result?.success) {
        message.success(`Rate "${key}" updated successfully!`);
      } else {
        message.error(`Failed to update "${key}"`);
      }
    } catch (err) {
      message.error(err.message || `Error saving "${key}"`);
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleHideSingleKey = async (key) => {
    try {
      setUpdatingKey(key);
      const result = await deleteConfig(key);
      if (result?.success) {
        message.success(`Rate config "${key}" hidden successfully!`);
      } else {
        message.error(`Failed to hide "${key}"`);
      }
    } catch (err) {
      message.error(err.message || `Error hiding "${key}"`);
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleUnhideSingleKey = async (key) => {
    try {
      setUpdatingKey(key);
      const result = await unhideConfig(key);
      if (result?.success) {
        message.success(`Rate config "${key}" unhidden successfully!`);
      } else {
        message.error(`Failed to unhide "${key}"`);
      }
    } catch (err) {
      message.error(err.message || `Error unhiding "${key}"`);
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleSaveJson = async () => {
    try {
      setUpdating(true);
      const parsed = JSON.parse(jsonText);
      const result = await updateConfig(parsed);
      if (result?.success) {
        message.success("JSON configuration updated successfully!");
      } else {
        message.error("Failed to update configuration");
      }
    } catch (err) {
      message.error("Invalid JSON format. Please check syntax.");
    } finally {
      setUpdating(false);
    }
  };

  const tabItems = useMemo(() => {
    const list = [];

    list.push({
      key: "all_rates",
      label: `All Rates (${filteredFormattedConfigs.length})`,
      children: (
        <div className="mt-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mb-4">
            <Input
              prefix={<SearchOutlined className="text-gray-400" />}
              placeholder="Search rate keys, friendly names, or values..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md h-11 rounded-lg text-[15px] border-gray-300"
            />
            <div className="flex items-center gap-2">
              <span className="font-gilroyBold text-gray-700 text-[14px]">Filter:</span>
              <Select
                value={adminFilter}
                onChange={(val) => setAdminFilter(val)}
                className="w-44 h-11 rounded-lg text-[14px]"
                options={[
                  { label: "Normal (Active)", value: "normal" },
                  { label: "Hidden", value: "hidden" },
                  { label: "All Rates", value: "all" },
                ]}
              />
            </div>
          </div>

          <CollapsibleRateList
            items={filteredFormattedConfigs}
            formValues={formValues}
            formDescriptions={formDescriptions}
            onFieldValueChange={handleFieldValueChange}
            onDescriptionChange={handleDescriptionChange}
            onSaveSingle={handleSaveSingleKey}
            onHideSingle={handleHideSingleKey}
            onUnhideSingle={handleUnhideSingleKey}
            updatingKey={updatingKey}
          />
        </div>
      ),
    });

    for (const group of dynamicCategories) {
      list.push({
        key: group.key,
        label: `${group.label} (${group.items.length})`,
        children: (
          <div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mb-4">
              <Input
                prefix={<SearchOutlined className="text-gray-400" />}
                placeholder="Search rate keys, friendly names, or values..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-md h-11 rounded-lg text-[15px] border-gray-300"
              />
              <div className="flex items-center gap-2">
                <span className="font-gilroyBold text-gray-700 text-[14px]">Filter:</span>
                <Select
                  value={adminFilter}
                  onChange={(val) => setAdminFilter(val)}
                  className="w-44 h-11 rounded-lg text-[14px]"
                  options={[
                    { label: "Normal (Active)", value: "normal" },
                    { label: "Hidden", value: "hidden" },
                    { label: "All Rates", value: "all" },
                  ]}
                />
              </div>
            </div>

            <CollapsibleRateList
              items={group.items}
              formValues={formValues}
              formDescriptions={formDescriptions}
              onFieldValueChange={handleFieldValueChange}
              onDescriptionChange={handleDescriptionChange}
              onSaveSingle={handleSaveSingleKey}
              onHideSingle={handleHideSingleKey}
              onUnhideSingle={handleUnhideSingleKey}
              updatingKey={updatingKey}
            />
          </div>
        ),
      });
    }

    return list;
  }, [dynamicCategories, filteredFormattedConfigs, searchQuery, adminFilter, formValues, formDescriptions, updatingKey]);

  return (
    <SupportLayout userRole="ADMIN">
      <Spin spinning={loading} indicator={antIcon}>
        <div className="pb-40">
          <div className="main_container_title_div mb-6 mt-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-gilroyBold">
                Rate Settings
              </h1>
              <p className="text-gray-600 text-xl sm:text-base mt-1">
                Manage system configurations, minimum order thresholds, vendor markups, and fees
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchConfig}
                loading={loading}
                className="h-10 font-gilroyBold rounded-lg text-[14px]"
              >
                Refresh
              </Button>
              <div className="flex items-center gap-2 bg-gray-100 p-1.5 rounded-lg border border-gray-200">
                <CodeOutlined className="text-gray-600 text-base ml-2" />
                <span className="text-sm font-semibold text-gray-700">JSON Mode</span>
                <Switch checked={jsonMode} onChange={(val) => setJsonMode(val)} />
              </div>
            </div>
          </div>

          {jsonMode ? (
            <Card className="shadow-sm rounded-2xl border-gray-200">
              <Form layout="vertical">
                <Form.Item label={<span className="font-gilroyBold text-base">Raw Configuration JSON</span>}>
                  <Input.TextArea
                    value={jsonText}
                    onChange={(e) => setJsonText(e.target.value)}
                    rows={18}
                    className="font-mono text-xs p-4 rounded-xl bg-gray-900 text-green-400"
                  />
                </Form.Item>
                <div className="flex justify-end gap-3">
                  <Button size="large" onClick={() => setJsonMode(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={updating}
                    onClick={handleSaveJson}
                    size="large"
                    className="bg-[#0e1726] hover:bg-[#1b2a4a] text-white font-gilroyBold px-8 border-none"
                  >
                    Update JSON Configuration
                  </Button>
                </div>
              </Form>
            </Card>
          ) : (
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                type="card"
                className="support-tabs"
                items={tabItems}
              />
            </div>
          )}
        </div>
      </Spin>
    </SupportLayout>
  );
};

export default AdminRateSettings;
